import { createSlice, createSelector } from "@reduxjs/toolkit";
import { getSkillPathsByDomainId } from "../../services/domainService";
import {
  getSkillPathGoals,
  getSkillPathSkills,
  getSkillPathDetails,
} from "../../services/skillPathService";
import { launchPadLayoutActions } from "./layoutReducer";

const skillPathSlice = createSlice({
  name: "skillPaths",
  initialState: [],
  reducers: {
    removeSkillPathDomainInfo(state, action) {
      const domainIdsToRemove = action.payload;
      const skillPathsAffected = state.filter((skillPath) =>
        Object.keys(skillPath.domainInfo).some((x) =>
          domainIdsToRemove.includes(x)
        )
      );

      skillPathsAffected.forEach((skillPath) => {
        domainIdsToRemove.forEach((domainId) => {
          if (skillPath.domainInfo) {
            delete skillPath.domainInfo[domainId];
          }
        });
      });
    },
    addSkillPaths(state, action) {
      state.push(action.payload);
    },
    updateSkillPaths(state, action) {
      action.payload.forEach((x) => {
        let stateSkillPath = state.find((y) => y.id === x.id);
        if (stateSkillPath) {
          stateSkillPath.completedCourses = x.completedCourses;
          stateSkillPath.completedDuration = x.completedDuration;
        }
      });
    },
    completeSkillPaths(state, action) {
      action.payload.forEach((id) => {
        let stateSkillPath = state.find((x) => x.id === id);
        if (stateSkillPath) {
          stateSkillPath.completedCourses = stateSkillPath.totalCourses;
          stateSkillPath.completedDuration = stateSkillPath.totalDuration;
        }
      });
    },
    addSkillPathGoals(state, action) {
      let stateSkillPath = state.find((x) => x.id === action.payload.id);
      if (stateSkillPath) {
        stateSkillPath.goals = action.payload.goals;
      } else {
        state.push({
          id: action.payload.id,
          goals: action.payload.goals,
        });
      }
    },
    addSkillPathSkills(state, action) {
      let stateSkillPath = state.find((x) => x.id === action.payload.id);
      if (stateSkillPath) {
        stateSkillPath.skills = action.payload.skills;
      } else {
        state.push({
          id: action.payload.id,
          skills: action.payload.skills,
        });
      }
    },
    upsertSkillPaths(state, action) {
      action.payload.skillPaths.forEach((skillPath) => {
        let stateSkillPath = state.find((x) => x.id === skillPath.skillPathId);
        //we create domain specific info for the skillpath
        //skill path rank and skill path level Id are specific to the domain
        let domainSpecificInfo = {
          skillPathRank: skillPath.rank,
          skillPathLevelId: skillPath.skillPathLevelId,
          refetch: action.payload.refetch,
        };
        //if skill path exists , it means it was loaded by another domain
        //so we just push the current domain's info about skill path
        //else we add the skill path to the state
        //with common properties accross all domains
        //and the domain specific info
        let newSkillPath = {
          id: skillPath.skillPathId,
          name: skillPath.skillPath,
          domainId: skillPath.domainId,
          domainName: skillPath.domainName,
          domainStatus: skillPath.domainStatus,
          backgroundImage: skillPath.backgroundImage,
          description: skillPath.skillPathDescription,
          level: skillPath.skillPathLevel,
          completedCourses: skillPath.completedCourses,
          totalCourses: skillPath.totalCourses,
          totalDuration: skillPath.totalDuration,
          completedDuration: skillPath.completedDuration,
          domainInfo: { [skillPath.domainId]: domainSpecificInfo },
          userBadgeId: skillPath.userBadgeId,
        };
        if (stateSkillPath) {
          Object.assign(stateSkillPath, newSkillPath);
        } else {
          state.push(newSkillPath);
        }
      });
    },
  },
});

export const skillPathActions = skillPathSlice.actions;

export const completeSkillPaths = (skillPaths) => {
  return (dispatch) => {
    // Add new completed skillpaths in an array to show the completion modal for each one
    const newSkillPaths = skillPaths.map((id) => ({ id }));
    dispatch(
      launchPadLayoutActions.setPendingCompletedSkillpaths(newSkillPaths)
    );
    dispatch(skillPathActions.completeSkillPaths(skillPaths));
  };
};

export const getDomainSkillPaths = (id) => {
  return async (dispatch, getState) => {
    const { skillPaths } = getState();

    //check in state if skill paths have in their domain info property
    //the domain id, if they dont we will request the domain skill paths from the api
    let domainSkillPaths = skillPaths.filter(
      (skillPath) => skillPath.domainInfo[id]
    );
    // if there are no skill paths for the domain
    // or if there are skill paths but they need to be refetched
    // we will request the domain skill paths from the api again
    if (
      domainSkillPaths.length === 0 ||
      domainSkillPaths.some((x) => x.domainInfo[id].refetch === true)
    ) {
      let response = await getSkillPathsByDomainId(id);
      dispatch(
        skillPathActions.upsertSkillPaths({
          skillPaths: response.data,
          refetch: false,
        })
      );
    }
  };
};

export const getSkillPath = (id) => {
  return async (dispatch, getState) => {
    const { skillPaths } = getState();
    if (!skillPaths.find((x) => x.id === id && x.totalCourses)) {
      let response = await getSkillPathDetails(id);
      dispatch(
        skillPathActions.upsertSkillPaths({
          skillPaths: [response.data],
          refetch: true,
        })
      );
    }
  };
};

export const getAllSkillPathGoals = (id) => {
  return async (dispatch, getState) => {
    const { skillPaths } = getState();

    let skillpath = skillPaths.find((x) => x.id === id);
    if (!skillpath || skillpath?.goals === undefined) {
      // Check if goals is undefined
      let response = await getSkillPathGoals(id);
      // First, sort the array by the 'order' property
      let sortedGoals = response.data.sort((a, b) => a.order - b.order);

      // Then, map over the sorted array to ensure each item has a name
      let namedGoals = sortedGoals.map((goal, index) => {
        // If the goal doesn't have a name, add one as 'Goal ' followed by the index + 1
        if (!goal.name) {
          return { ...goal, name: `Goal ${index + 1}` };
        }
        return goal;
      });
      dispatch(
        skillPathActions.addSkillPathGoals({ id: id, goals: namedGoals })
      );
    }
  };
};

export const getAllSkillPathSkills = (id) => {
  return async (dispatch, getState) => {
    const { skillPaths } = getState();

    let skillpath = skillPaths.find((x) => x.id === id);
    if (!skillpath || skillpath?.skills === undefined) {
      // Check if skills is undefined
      let response = await getSkillPathSkills(id);
      dispatch(
        skillPathActions.addSkillPathSkills({ id: id, skills: response.data })
      );
    }
  };
};

// -------------------------------------------------  Selectors
const selectAllSkillPaths = (state) => state.skillPaths;

export const selectAllDomainSkillPaths = createSelector(
  [selectAllSkillPaths, (state, domainId) => domainId],
  (skillPaths, domainId) =>
    skillPaths
      .filter((sp) => sp.domainInfo[domainId])
      .map((sp) => {
        //Assuming skill path will belong to the domain only once
        let info = sp.domainInfo[domainId];
        //we simply flatten the domain info object into the skill path object
        return {
          ...sp,
          domainId,
          skillPathLevelId: info.skillPathLevelId,
          rank: info.skillPathRank,
        };
      })
);

export const selectDomainSkillPathsBySkillPathLevel = createSelector(
  [
    selectAllSkillPaths,
    selectAllDomainSkillPaths,
    (state, domainId, skillPathLevelId) => ({ domainId, skillPathLevelId }),
  ],
  (allSkillPaths, domainSkillPaths, { domainId, skillPathLevelId }) => {
    return domainSkillPaths
      .filter((sp) => sp.skillPathLevelId === skillPathLevelId)
      .sort((a, b) => a.rank - b.rank);
  }
);

export default skillPathSlice.reducer;
